import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  deliverRequirement,
  isPortalHttpError,
  OfferOutcome,
  PortalOffer,
  PortalRequirement,
  PortalStatus,
} from './api/portalService';
import { clientPortalTraductor } from './traductor/clientPortal';
import { usePortalQuery } from './hooks/usePortalQuery';

export type OfferOutcomeFn = (outcome: OfferOutcome) => Promise<void>;
/** OUW-70: client confirms MX as-is approval (no modal). */
export type ConfirmSessionFn = () => Promise<void>;

export type ModalId =
  | 'schedule'
  | 'upload'
  | 'sign'
  | 'quest'
  | 'reply'
  | 'accept'
  | 'decline'
  | 'pay'
  | 'notify';

export type PayMode = 'pay' | 'method';

type PortalUiValue = {
  doneRows: Record<string, boolean>;
  toast: string | null;
  modal: ModalId | null;
  step: number;
  targetId: string | null;
  uploadTitle: string;
  payMode: PayMode;
  /** OUW-200 (H4): la prima que se cobra, ya formateada; null = sin dato en vivo. */
  payAmount: string | null;
  liveRequirement: PortalRequirement | null;
  openModal: (id: ModalId) => void;
  openUpload: (rowId: string, title: string) => void;
  openLiveUpload: (requirement: PortalRequirement) => void;
  openLiveReply: (requirement: PortalRequirement) => void;
  /** OUW-260: proponer horarios para la cita que agenda Olé. */
  openLiveSchedule: (requirement: PortalRequirement) => void;
  deliverLiveRequirement: (requirement: PortalRequirement) => Promise<void>;
  openTarget: (id: ModalId, rowId: string) => void;
  openPay: (mode: PayMode, amount?: string | null) => void;
  closeModal: () => void;
  setStep: (n: number) => void;
  markDone: () => void;
  showToast: (msg: string) => void;
  isDone: (id: string) => boolean;
  offerOutcome?: OfferOutcomeFn;
  setOfferOutcome: (fn: OfferOutcomeFn | undefined) => void;
  confirmSession?: ConfirmSessionFn;
  setConfirmSession: (fn: ConfirmSessionFn | undefined) => void;
  liveOffer: PortalOffer | null;
  liveOfferLabels: Record<string, string>;
  setLiveOffer: (offer: PortalOffer | null, labels?: Record<string, string>) => void;
  /**
   * OUW-88: full live application (not only agent). Decline modal / CTAs
   * decide mock vs live with `mocked`, never by null-coalescing to the fixture.
   */
  liveStatus: PortalStatus | null;
  setLiveStatus: (status: PortalStatus | null) => void;
  /** True when the board is driven by `?state=` (prototype), not the API. */
  mocked: boolean;
  requirementRefetch?: () => void;
  setRequirementRefetch: (fn: (() => void) | undefined) => void;
};

const PortalUiContext = createContext<PortalUiValue | null>(null);

export function PortalUiProvider({ children }: { children: React.ReactNode }) {
  const { lang, state, quoteID } = usePortalQuery();
  const mocked = Boolean(state);
  const t = useCallback(
    (key: Parameters<typeof clientPortalTraductor>[1]) => clientPortalTraductor(lang, key),
    [lang],
  );
  const [doneRows, setDoneRows] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<string | null>(null);
  const [toastTimer, setToastTimer] = useState<number | null>(null);
  const [modal, setModal] = useState<ModalId | null>(null);
  const [step, setStep] = useState(1);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [uploadTitle, setUploadTitle] = useState('Documento');
  const [payMode, setPayMode] = useState<PayMode>('pay');
  const [payAmount, setPayAmount] = useState<string | null>(null);
  const [offerOutcome, setOfferOutcomeState] = useState<OfferOutcomeFn | undefined>();
  const [confirmSession, setConfirmSessionState] = useState<ConfirmSessionFn | undefined>();
  const [liveOffer, setLiveOfferState] = useState<PortalOffer | null>(null);
  const [liveOfferLabels, setLiveOfferLabels] = useState<Record<string, string>>({});
  const [liveStatus, setLiveStatusState] = useState<PortalStatus | null>(null);
  const [liveRequirement, setLiveRequirement] = useState<PortalRequirement | null>(null);
  const [requirementRefetch, setRequirementRefetchState] = useState<(() => void) | undefined>();

  const setOfferOutcome = useCallback((fn: OfferOutcomeFn | undefined) => {
    setOfferOutcomeState(() => fn);
  }, []);

  const setConfirmSession = useCallback((fn: ConfirmSessionFn | undefined) => {
    setConfirmSessionState(() => fn);
  }, []);

  const setRequirementRefetch = useCallback((fn: (() => void) | undefined) => {
    setRequirementRefetchState(() => fn);
  }, []);

  const setLiveOffer = useCallback((offer: PortalOffer | null, labels: Record<string, string> = {}) => {
    setLiveOfferState(offer);
    setLiveOfferLabels(labels);
  }, []);

  const setLiveStatus = useCallback((status: PortalStatus | null) => {
    setLiveStatusState(status);
  }, []);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimer) window.clearTimeout(toastTimer);
    const id = window.setTimeout(() => setToast(null), 3400);
    setToastTimer(id);
  }, [toastTimer]);

  const closeModal = useCallback(() => {
    setModal(null);
    setStep(1);
    setTargetId(null);
    setLiveRequirement(null);
  }, []);

  const openModal = useCallback((id: ModalId) => {
    setLiveRequirement(null);
    setModal(id);
    setStep(1);
    setTargetId(null);
  }, []);

  const openUpload = useCallback((rowId: string, title: string) => {
    setLiveRequirement(null);
    setUploadTitle(title);
    setTargetId(rowId);
    setModal('upload');
    setStep(1);
  }, []);

  const openLiveUpload = useCallback((requirement: PortalRequirement) => {
    setLiveRequirement(requirement);
    setUploadTitle(requirement.clientLabel || 'Documento');
    setTargetId(requirement.id);
    setModal('upload');
    setStep(1);
  }, []);

  const openLiveReply = useCallback((requirement: PortalRequirement) => {
    setLiveRequirement(requirement);
    setTargetId(requirement.id);
    setModal('reply');
    setStep(1);
  }, []);
  const openLiveSchedule = useCallback((requirement: PortalRequirement) => {
    setLiveRequirement(requirement);
    setTargetId(requirement.id);
    setModal('schedule');
    setStep(1);
  }, []);

  const openTarget = useCallback((id: ModalId, rowId: string) => {
    setLiveRequirement(null);
    setTargetId(rowId);
    setModal(id);
    setStep(1);
  }, []);

  const openPay = useCallback((mode: PayMode, amount?: string | null) => {
    setLiveRequirement(null);
    setPayMode(mode);
    setPayAmount(amount ?? null);
    setModal('pay');
    setStep(1);
  }, []);

  const markDone = useCallback(() => {
    if (targetId) {
      setDoneRows((prev) => ({ ...prev, [targetId]: true }));
    }
    closeModal();
    showToast(t('toastReceived'));
  }, [targetId, closeModal, showToast, t]);

  const deliverLiveRequirement = useCallback(async (requirement: PortalRequirement) => {
    try {
      await deliverRequirement(quoteID, requirement.id);
    } catch (error) {
      if (isPortalHttpError(error) && error.status === 409) {
        requirementRefetch?.();
        return;
      }
      showToast(error instanceof Error ? error.message : t('reqLoadError'));
      return;
    }
    showToast(t('toastReceived'));
    requirementRefetch?.();
  }, [quoteID, requirementRefetch, showToast, t]);

  const value = useMemo<PortalUiValue>(
    () => ({
      doneRows,
      toast,
      modal,
      step,
      targetId,
      uploadTitle,
      payMode,
      payAmount,
      liveRequirement,
      openModal,
      openUpload,
      openLiveUpload,
      openLiveReply,
      openLiveSchedule,
      deliverLiveRequirement,
      openTarget,
      openPay,
      closeModal,
      setStep,
      markDone,
      showToast,
      isDone: (id: string) => Boolean(doneRows[id]),
      offerOutcome,
      setOfferOutcome,
      confirmSession,
      setConfirmSession,
      liveOffer,
      liveOfferLabels,
      setLiveOffer,
      liveStatus,
      setLiveStatus,
      mocked,
      requirementRefetch,
      setRequirementRefetch,
    }),
    [
      doneRows,
      toast,
      modal,
      step,
      targetId,
      uploadTitle,
      payMode,
      payAmount,
      liveRequirement,
      openModal,
      openUpload,
      openLiveUpload,
      openLiveReply,
      openLiveSchedule,
      deliverLiveRequirement,
      openTarget,
      openPay,
      closeModal,
      setStep,
      markDone,
      showToast,
      offerOutcome,
      setOfferOutcome,
      confirmSession,
      setConfirmSession,
      liveOffer,
      liveOfferLabels,
      setLiveOffer,
      liveStatus,
      setLiveStatus,
      mocked,
      requirementRefetch,
      setRequirementRefetch,
    ],
  );

  return <PortalUiContext.Provider value={value}>{children}</PortalUiContext.Provider>;
}

export function usePortalUi(): PortalUiValue {
  const ctx = useContext(PortalUiContext);
  if (!ctx) throw new Error('usePortalUi must be used inside PortalUiProvider');
  return ctx;
}

export function usePortalCopy() {
  const { lang } = usePortalQuery();
  return useCallback(
    (key: Parameters<typeof clientPortalTraductor>[1], vars?: Record<string, string | number>) =>
      clientPortalTraductor(lang, key, vars),
    [lang],
  );
}
